import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useAdoptedAnimals, useMyAdoptionDecisions, generateBehavioralAdvice, getImageUrl, createCheckIn, toast, useAdoptionLeadByAnimal,
  getAvailableCheckInSlots, getCheckInStatus, sortByCheckInUrgency, checkInStatusLabel,
} from '@rehome/shared';
import type { Animal, CheckInStatus } from '@rehome/shared';
import { Camera, Clock, CheckCircle2, Send, Bot, Sparkles, Smile, Frown, Meh, PawPrint, ChevronLeft, ChevronRight, Loader2, X, RefreshCw, AlertTriangle, MessageSquare } from 'lucide-react';

/** Max number of upcoming check-in slots to show (excluding "Spontán") */
const MAX_UPCOMING_SLOTS = 2;

/**
 * A helyzetjelentés szöveges beszámolójának hossz-korlátai.
 *
 * A minimum nem önkényes szigor: egy "minden ok" típusú egysoros semmit nem
 * mond a menhelynek arról, hogyan alakult az állat élete a legutóbbi jelentés
 * óta — márpedig az utókövetésnek pont ez a lényege. A maximum pedig azért
 * kell, hogy a menhelyi munkatárs át tudja futni a beszámolókat.
 */
const CHECKIN_NOTES_MIN = 200;
const CHECKIN_NOTES_MAX = 1000;

/** Per-animal workspace state — cached across animal switches within the session. */
interface WorkspaceState {
  photo: string | null;
  timestamp: string | null;
  mood: 'Happy' | 'Neutral' | 'Struggling' | null;
  /** A kötelező szöveges beszámoló — piszkozatként is megmarad állatváltáskor. */
  notes: string;
  checkInSubmitted: boolean;
  checkInType: string;
  messages: { id: string; sender: 'user' | 'bot'; text: string }[];
}

/**
 * Adopter Portal — horizontal animal selector + check-in/support workspace.
 * Desktop-friendly layout. Per-animal state cached within the session.
 */
const AdopterPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('animalId');
  const { data: animalsData, loading } = useAdoptedAnimals();
  // Az elutasított igénylések NEM jönnek elő az állatlistából: elutasításkor
  // az `animals.adopter_id` kiürül, tehát az állat visszakerül a gazdikeresők
  // közé. A menhely indoklása enélkül sosem jutna el a címzettjéhez.
  const { decisions: rejectedDecisions } = useMyAdoptionDecisions();
  // Elöl azok, ahol időszerű a helyzetjelentés (a késettek legelöl). Több
  // állatnál a vízszintes választó könnyen elrejtheti azt, amelyikkel épp
  // teendő van — a sorrend maga az első jelzés.
  const animals = useMemo(() => sortByCheckInUrgency(animalsData ?? []), [animalsData]);
  const [selectedId, setSelectedId] = useState<string | null>(preselectedId || null);
  // Hány állatnál időszerű most jelentés — ez adja a lista fölötti összegzést.
  const dueAnimals = useMemo(() => animals.filter(a => getCheckInStatus(a).needsAttention), [animals]);
  const selectedAnimal = useMemo(() => animals.find(a => a.id === selectedId) || null, [animals, selectedId]);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Per-animal state cache
  const workspaceCache = useRef<Map<string, WorkspaceState>>(new Map());

  // #1: Default to first animal when data loads and nothing is selected
  useEffect(() => {
    if (animals.length > 0 && !selectedId) {
      setSelectedId(preselectedId || animals[0]?.id || null);
    }
  }, [animals, preselectedId, selectedId]);

  const scroll = useCallback((dir: 'left' | 'right') => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  }, []);

  if (loading && animals.length === 0) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (animals.length === 0) {
    return (
      <div className="max-w-3xl mx-auto">
      {/* Az elutasítás akkor is látszik, ha nincs egyetlen állat sem —
          épp ilyenkor a leggyakoribb, hogy ez az egyetlen visszajelzés. */}
      <RejectedDecisionList decisions={rejectedDecisions} />
      <div className="text-center py-16">
        <PawPrint className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Még nincs örökbefogadott állatod</h2>
        <p className="text-gray-500 mb-4">Ha örökbe fogadtál egy állatot, itt fogod tudni követni az életét.</p>
        <Link to="/animals" className="text-teal-600 font-semibold hover:underline">Nézz szét a gazdikeresők között!</Link>
      </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <RejectedDecisionList decisions={rejectedDecisions} />

      {/* Horizontal Animal Selector */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Saját Állataim</h1>

        {dueAnimals.length > 0 && (
          <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-red-800">
                {dueAnimals.length === 1
                  ? `Időszerű a helyzetjelentés: ${dueAnimals[0].name}`
                  : `${dueAnimals.length} állatodnál időszerű a helyzetjelentés`}
              </p>
              <p className="text-red-700/80">
                {dueAnimals.map(a => a.name).join(', ')} — válaszd ki lent az állatot, és küldd be a jelentést.
              </p>
            </div>
          </div>
        )}

        <div className="relative group">
          {animals.length > 3 && (
            <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full p-2 hover:bg-teal-50 transition-colors opacity-0 group-hover:opacity-100">
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2 snap-x" style={{ scrollbarWidth: 'thin' }}>
            {animals.map(animal => {
              const status = getCheckInStatus(animal);
              const label = checkInStatusLabel(status);
              return (
                <button key={animal.id} onClick={() => setSelectedId(animal.id)}
                  // A kiválasztás (teal) és a figyelemfelhívás (piros) két külön
                  // jelzés: a piros keret akkor is látszik, ha épp más állat van
                  // kiválasztva, különben a teendő eltűnne a kiválasztás alatt.
                  className={`relative flex-shrink-0 snap-start w-44 rounded-2xl border-2 p-3 transition-all text-left ${
                    selectedId === animal.id
                      ? 'border-teal-500 bg-teal-50 shadow-md'
                      : status.needsAttention
                        ? 'border-red-300 bg-red-50/60 hover:border-red-400 hover:shadow-sm'
                        : 'border-gray-100 bg-white hover:border-teal-200 hover:shadow-sm'
                  }`}>
                  {status.needsAttention && (
                    <span
                      title={label ?? 'Időszerű a helyzetjelentés'}
                      className="absolute -top-2 -right-2 z-10 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center shadow-md ring-2 ring-white"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span className="sr-only">{label ?? 'Időszerű a helyzetjelentés'}</span>
                    </span>
                  )}
                  <div className="w-full aspect-square rounded-xl bg-gray-200 overflow-hidden mb-3">
                    {getImageUrl(animal) ? <img src={getImageUrl(animal)!} alt={animal.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-gray-400"><PawPrint className="w-8 h-8" /></div>}
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm truncate">{animal.name}</h3>
                  <p className="text-xs text-gray-500 truncate">{animal.breed} · {animal.species}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{animal.age} éves</p>
                  {label && (
                    <p className={`text-[11px] font-semibold mt-1.5 leading-tight ${
                      status.urgency === 'overdue' ? 'text-red-700' : status.urgency === 'due' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {label}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
          {animals.length > 3 && (
            <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full p-2 hover:bg-teal-50 transition-colors opacity-0 group-hover:opacity-100">
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Workspace — key={animal.id} ensures fresh state when switching animals */}
      {selectedAnimal ? <AnimalWorkspace key={selectedAnimal.id} animal={selectedAnimal} cache={workspaceCache} />
        : <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400"><PawPrint className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Válassz egy állatot fent a folytatáshoz</p></div>}
    </div>
  );
};

// ─── Workspace ──────────────────────────────────────────────────────────

function getCachedState(
  cache: React.MutableRefObject<Map<string, WorkspaceState>>,
  animalId: string,
  defaultCheckInType: string,
): WorkspaceState {
  if (!cache.current.has(animalId)) {
    cache.current.set(animalId, {
      photo: null, timestamp: null, mood: null, notes: '', checkInSubmitted: false,
      // Ha van esedékes mérföldkő, azt választjuk elő — a "Spontán" alapérték
      // mellett az örökbefogadó könnyen a rossz típussal küldte be az épp
      // esedékes jelentést, ami így sosem számított teljesítettnek.
      checkInType: defaultCheckInType,
      messages: [],
    });
  }
  return cache.current.get(animalId)!;
}

/**
 * Wrapper that routes to the pending-adoption view or the check-in workspace.
 * IMPORTANT: this split exists to avoid a conditional-hooks violation — the
 * old code early-returned between hook calls, which crashes React when an
 * animal's status changes (e.g. AdoptionPending → Adopted) without remount.
 */
const AnimalWorkspace: React.FC<{ animal: Animal; cache: React.MutableRefObject<Map<string, WorkspaceState>> }> = ({ animal, cache }) => {
  if (animal.status === 'AdoptionPending') {
    return <PendingAdoptionCard animal={animal} />;
  }
  return <CheckInWorkspace animal={animal} cache={cache} />;
};

/**
 * Elutasított örökbefogadási igénylések a menhely indoklásával.
 *
 * Nem apró széljegyzet: ez az egyetlen személyes visszajelzés, amit az
 * igénylő a döntésről kap, és az állat maga már nem szerepel a felületén.
 * Az indoklás ezért idézetként, olvasható tördeléssel áll — a menhely
 * mondata, nem rendszerüzenet.
 */
const RejectedDecisionList: React.FC<{
  decisions: Array<{
    id: string; animalId: string; animalName: string; animalImageUrl: string | null;
    reviewNote: string | null; reviewedAt: string | null;
  }>;
}> = ({ decisions }) => {
  if (decisions.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      {decisions.map(d => (
        <div key={d.id} className="bg-white border border-red-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-red-50 px-5 py-3 flex items-center gap-3 border-b border-red-100">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-red-100 flex-shrink-0">
              {d.animalImageUrl
                ? <img src={d.animalImageUrl} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-5 h-5 text-red-400" /></div>}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-red-900 truncate">
                {d.animalName} — az igénylésed elutasítva
              </h3>
              {d.reviewedAt && (
                <p className="text-xs text-red-700/70">
                  {new Date(d.reviewedAt).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>
          </div>

          <div className="px-5 py-4">
            {d.reviewNote ? (
              <>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">A menhely üzenete</p>
                <blockquote className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap border-l-2 border-red-300 pl-3">
                  {d.reviewNote}
                </blockquote>
              </>
            ) : (
              <p className="text-sm text-gray-500">A menhely nem adott meg indoklást.</p>
            )}
            <Link to="/animals" className="inline-block mt-3 text-sm text-teal-700 font-semibold hover:underline">
              Nézz szét a többi gazdikereső között →
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══ PENDING ADOPTION STATE ═══
const PendingAdoptionCard: React.FC<{ animal: Animal }> = ({ animal }) => {
  const { lead } = useAdoptionLeadByAnimal(animal.id);

  const statusLabel: Record<string, string> = {
    Pending: 'Elbírálás alatt',
    Approved: 'Elfogadva — folytathatod a véglegesítést!',
    Rejected: 'Elutasítva',
    Contacted: 'A menhely felvette veled a kapcsolatot',
  };
  const leadStatus = lead?.status || 'Pending';
  const isRejected = leadStatus === 'Rejected';
  const isApproved = leadStatus === 'Approved';

  // A menhely indoklása. A `reviewNote` mindkét döntésnél kitöltött; a
  // `rejectionReason` csak a 045-ös migráció ELŐTTI elutasításoknál marad
  // egyedüli forrás, ezért tartalékként szerepel.
  const reviewNote = lead?.reviewNote || lead?.rejectionReason || null;
  const reviewedAt = lead?.reviewedAt
    ? new Date(lead.reviewedAt).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-amber-600 to-amber-500 text-white p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 overflow-hidden flex-shrink-0">
          {getImageUrl(animal) ? <img src={getImageUrl(animal)!} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-6 h-6 text-white/60" /></div>}
        </div>
        <div><h2 className="font-bold text-lg">{animal.name}</h2><p className="text-sm text-amber-100">Örökbefogadás folyamatban</p></div>
      </div>
      <div className="p-6 text-center space-y-4">
        <Clock className="w-12 h-12 text-amber-500 mx-auto" />
        {/* A cím kövesse a tényleges állapotot: eddig egy elfogadott igénylés
            fölött is az "elbírálás alatt" felirat állt, közvetlenül az
            "Elfogadva" szöveg fölött. */}
        <h3 className="text-xl font-bold text-gray-800">
          {isRejected ? 'Igénylés elutasítva'
            : isApproved ? 'Igénylésed elfogadva!'
            : 'Örökbefogadási igény elbírálás alatt'}
        </h3>
        <p className="text-gray-500">{statusLabel[leadStatus] || 'A menhely még nem bírálta el az igénylésed.'}</p>
        {/* ── A menhely döntése ──────────────────────────────────────────
            Az indoklás mindkét kimenetnél megjelenik, és nem apró
            széljegyzetként: ez az egyetlen személyes visszajelzés, amit az
            örökbefogadó a döntésről kap. A megszólítás ezért idézetként áll,
            nem hibaüzenet-szerű sávban. */}
        {(isRejected || isApproved) && reviewNote && (
          <div className={`rounded-xl border p-5 text-left ${
            isApproved ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className={`w-4 h-4 ${isApproved ? 'text-green-700' : 'text-red-700'}`} />
              <h4 className={`text-sm font-bold ${isApproved ? 'text-green-900' : 'text-red-900'}`}>
                A menhely üzenete
              </h4>
              {reviewedAt && <span className="text-xs text-gray-400 ml-auto">{reviewedAt}</span>}
            </div>
            <blockquote className={`text-sm leading-relaxed whitespace-pre-wrap border-l-2 pl-3 ${
              isApproved ? 'text-green-900 border-green-300' : 'text-red-900 border-red-300'
            }`}>
              {reviewNote}
            </blockquote>
          </div>
        )}
        {!isRejected && !isApproved && (
          <p className="text-sm text-gray-400">Amint a menhely elfogadja az igénylésed, itt tudod majd véglegesíteni az örökbefogadást.</p>
        )}
        {leadStatus === 'Approved' && (
          <Link to={`/adoption/${animal.id}?finalize=true`} className="inline-block bg-teal-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-teal-700">
            Véglegesítés folytatása →
          </Link>
        )}
      </div>
    </div>
  );
};

// ═══ CHECK-IN WORKSPACE ═══
const CheckInWorkspace: React.FC<{ animal: Animal; cache: React.MutableRefObject<Map<string, WorkspaceState>> }> = ({ animal, cache }) => {
  const checkInStatus: CheckInStatus = getCheckInStatus(animal);
  const availableSlots = getAvailableCheckInSlots(animal).slice(0, MAX_UPCOMING_SLOTS);
  // Csak olyan típust választunk elő, ami ténylegesen szerepel a legördülőben.
  // A `checkInStatus` egy rég lejárt mérföldkövet is jelezhet, aminek a
  // beküldési ablaka már bezárult — azt kiválasztva a select üresen állna,
  // miközben a state egy nem választható értéket tartana.
  const defaultCheckInType =
    availableSlots.find(s => s.value === checkInStatus.milestone?.value)?.value ?? 'Spontaneous';
  const cached = getCachedState(cache, animal.id, defaultCheckInType);
  const [activeTab, setActiveTab] = useState<'checkin' | 'support'>('checkin');
  const [photo, setPhoto] = useState<string | null>(cached.photo);
  const [timestamp, setTimestamp] = useState<string | null>(cached.timestamp);
  const [mood, setMood] = useState<'Happy' | 'Neutral' | 'Struggling' | null>(cached.mood);
  const [notes, setNotes] = useState<string>(cached.notes);
  const [checkInSubmitted, setCheckInSubmitted] = useState(cached.checkInSubmitted);
  const [checkInType, setCheckInType] = useState<string>(cached.checkInType);
  const initMsgs = cached.messages.length > 0 ? cached.messages : [{
    id: '1', sender: 'bot' as const,
    text: `Szia! ${animal.name} kapcsán kérdezhetsz — ${animal.species.toLowerCase()}, ${animal.breed || 'ismeretlen fajta'}, ${animal.age} éves. Miben segíthetek?`,
  }];
  const [messages, setMessages] = useState<{ id: string; sender: 'user' | 'bot'; text: string }[]>(initMsgs);

  // Persist state to cache on every change
  const persist = () => {
    cache.current.set(animal.id, { photo, timestamp, mood, notes, checkInSubmitted, checkInType, messages });
  };
  useEffect(() => { persist(); }, [photo, timestamp, mood, notes, checkInSubmitted, checkInType, messages]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleTakePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleDeletePhoto = () => {
    setPhoto(null);
    setTimestamp(null);
    // Reset the file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhoto(ev.target?.result as string);
        setTimestamp(new Date().toLocaleString());
      };
      reader.readAsDataURL(file);
    }
  };

  const trimmedNotes = notes.trim();
  const notesLength = trimmedNotes.length;
  const notesTooShort = notesLength < CHECKIN_NOTES_MIN;
  const canSubmit = !!photo && !!mood && !notesTooShort;

  const handleSubmitCheckIn = async () => {
    if (!photo || !mood) { toast.error('Kérlek tölts fel egy fotót és jelöld be a hangulatot!'); return; }
    if (notesTooShort) {
      toast.error(`Írj le legalább ${CHECKIN_NOTES_MIN} karakterben, mi történt a legutóbbi helyzetjelentés óta (jelenleg ${notesLength}).`);
      return;
    }
    setSubmitting(true);
    const success = await createCheckIn({
      animalId: animal.id,
      type: checkInType,
      imageUrl: photo,
      mood,
      notes: trimmedNotes,
    });
    setSubmitting(false);
    if (success) {
      setCheckInSubmitted(true);
      // A beküldött beszámoló nem maradhat a következő jelentés piszkozatában:
      // az utókövetés lényege, hogy minden jelentés az AZÓTA eltelt időszakról
      // szóljon, nem az előző szövegének újraküldéséről.
      setNotes('');
    }
  };
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { id: Date.now().toString(), sender: 'user' as const, text: chatInput };
    setMessages(prev => [...prev, userMsg]); setChatInput(''); setIsTyping(true);
    const advice = await generateBehavioralAdvice(`${animal.name} (${animal.species}, ${animal.breed || 'ismeretlen'}, ${animal.age} éves): ${userMsg.text}`, animal.species);
    setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), sender: 'bot', text: advice }]); setIsTyping(false);
  };

  const moodOptions = [
    { mood: 'Happy' as const, icon: Smile, label: 'Szuperül', color: 'green', activeClass: 'border-green-500 bg-green-50 shadow-sm', iconColor: 'text-green-600' },
    { mood: 'Neutral' as const, icon: Meh, label: 'Alakul', color: 'yellow', activeClass: 'border-yellow-500 bg-yellow-50 shadow-sm', iconColor: 'text-yellow-600' },
    { mood: 'Struggling' as const, icon: Frown, label: 'Nehezen', color: 'red', activeClass: 'border-red-500 bg-red-50 shadow-sm', iconColor: 'text-red-600' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 overflow-hidden flex-shrink-0">
          {getImageUrl(animal) ? <img src={getImageUrl(animal)!} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-6 h-6 text-white/60" /></div>}
        </div>
        <div><h2 className="font-bold text-lg">{animal.name}</h2><p className="text-sm text-teal-100">{animal.breed} · {animal.species} · {animal.age} éves</p></div>
      </div>

      {/* Esedékesség-sáv: a listában látott piros jelzés magyarázata itt, a
          tényleges munkafelület tetején — hogy ne kelljen visszagörgetni. */}
      {checkInStatus.needsAttention && !checkInSubmitted && (
        <div className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b ${
          checkInStatus.urgency === 'overdue'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{checkInStatusLabel(checkInStatus)} — küldd be alább.</span>
        </div>
      )}

      <div className="flex border-b border-gray-200">
        <button onClick={() => setActiveTab('checkin')} className={`flex-1 py-3 font-bold text-sm flex items-center justify-center gap-2 border-b-2 ${activeTab === 'checkin' ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-teal-600'}`}><CheckCircle2 className="w-4 h-4" /> Helyzetjelentés</button>
        <button onClick={() => setActiveTab('support')} className={`flex-1 py-3 font-bold text-sm flex items-center justify-center gap-2 border-b-2 ${activeTab === 'support' ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-teal-600'}`}><Bot className="w-4 h-4" /> AI Tanácsadó</button>
      </div>
      <div className="p-6 min-h-[400px]">
        {activeTab === 'checkin' && !checkInSubmitted && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-bold text-gray-800">Kötelező fotó</h3>
              <p className="text-sm text-gray-500">Tölts fel egy friss képet {animal.name} nevű {animal.species.toLowerCase()}dról.</p>
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
              {!photo ? (
                <button onClick={handleTakePhoto} className="w-full aspect-video border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors text-gray-400"><Camera className="w-10 h-10 mb-2" /><span>Fotó készítése</span></button>
              ) : (
                <div className="relative rounded-xl overflow-hidden shadow-md group">
                  <img src={photo} alt="Checkin" className="w-full aspect-video object-cover" />
                  {/* Action buttons — appear on hover */}
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={handleTakePhoto} title="Kép cseréje" className="bg-white/90 hover:bg-white text-gray-700 rounded-lg p-1.5 shadow transition-colors">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button onClick={handleDeletePhoto} title="Kép törlése" className="bg-white/90 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-lg p-1.5 shadow transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-xs flex justify-between"><span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {timestamp}</span></div>
                </div>
              )}
              {/* Check-in type selector — shows only upcoming slots within window */}
              <div className="pt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Helyzetjelentés típusa</label>
                <select value={checkInType} onChange={e => setCheckInType(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm">
                  <option value="Spontaneous">Spontán</option>
                  {availableSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
                {availableSlots.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Nincs esedékes időszakos jelentés. Használd a Spontán opciót!</p>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="font-bold text-gray-800">Hogy érzi magát {animal.name}?</h3>
              <div className="grid grid-cols-3 gap-3">
                {moodOptions.map(item => (
                  <button key={item.mood} onClick={() => setMood(item.mood)} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${mood === item.mood ? item.activeClass : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <item.icon className={`w-8 h-8 ${mood === item.mood ? item.iconColor : 'text-gray-300'}`} /><span className="text-xs font-bold text-gray-600">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Kötelező szöveges beszámoló. A számláló mindig látszik, és
                  amíg kevés a szöveg, a hiányzó karakterszámot mutatja — így
                  a felhasználó nem a beküldés gombnál szembesül a hibával. */}
              <div className="space-y-1.5">
                <label htmlFor="checkin-notes" className="block font-bold text-gray-800">
                  Mi történt a legutóbbi helyzetjelentés óta? <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500">
                  Írd le, hogyan telt {animal.name} elmúlt időszaka: beilleszkedés, egészség, szokások, esetleges nehézségek.
                </p>
                <textarea
                  id="checkin-notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value.slice(0, CHECKIN_NOTES_MAX))}
                  maxLength={CHECKIN_NOTES_MAX}
                  rows={7}
                  aria-describedby="checkin-notes-counter"
                  placeholder={`Pl. ${animal.name} teljesen felengedett az elmúlt hetekben...`}
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm outline-none resize-y focus:ring-2 ${
                    notesTooShort && notesLength > 0
                      ? 'border-amber-300 bg-amber-50/40 focus:ring-amber-400'
                      : 'border-gray-300 focus:ring-teal-500'
                  }`}
                />
                <div id="checkin-notes-counter" aria-live="polite" className="flex items-center justify-between text-xs">
                  <span className={notesTooShort ? 'text-amber-700 font-semibold' : 'text-green-700 font-semibold'}>
                    {notesTooShort
                      ? `Még ${CHECKIN_NOTES_MIN - notesLength} karakter szükséges`
                      : 'Megfelelő hosszúságú ✓'}
                  </span>
                  <span className={`font-mono ${notesLength >= CHECKIN_NOTES_MAX ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                    {notesLength} / {CHECKIN_NOTES_MAX}
                  </span>
                </div>
                {/* Haladássáv: a minimumig tartó út vizuálisan is követhető */}
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${notesTooShort ? 'bg-amber-400' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(100, Math.round((notesLength / CHECKIN_NOTES_MIN) * 100))}%` }}
                  />
                </div>
              </div>

              <button
                onClick={handleSubmitCheckIn}
                disabled={!canSubmit || submitting}
                className={`w-full py-3 rounded-xl font-bold transition-all ${
                  !canSubmit || submitting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm hover:shadow'
                }`}
              >
                {submitting ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Beküldés...</span> : 'Jelentés Beküldése'}
              </button>
            </div>
          </div>
        )}
        {activeTab === 'checkin' && checkInSubmitted && (
          <div className="text-center py-12"><div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="w-10 h-10" /></div><h2 className="text-2xl font-bold text-gray-800 mb-2">Köszönjük!</h2><p className="text-gray-600">{animal.name} jelentését sikeresen rögzítettük. A menhely munkatársai hamarosan látni fogják.</p><button onClick={() => setCheckInSubmitted(false)} className="mt-6 text-teal-600 font-bold hover:underline">Új jelentés küldése</button></div>
        )}
        {activeTab === 'support' && (
          <div className="flex flex-col" style={{ height: '400px' }}>
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2">
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] p-3 rounded-2xl ${msg.sender === 'user' ? 'bg-teal-600 text-white rounded-br-md' : 'bg-gray-50 border border-gray-100 text-gray-700 rounded-bl-md'}`}>
                    <div className="flex items-center gap-1 mb-1">{msg.sender === 'bot' ? <Sparkles className="w-3 h-3 text-purple-500" /> : null}<span className="text-[10px] font-bold opacity-70">{msg.sender === 'bot' ? `AI (${animal.name})` : 'Te'}</span></div>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ))}
              {isTyping && <div className="flex justify-start"><div className="bg-gray-50 border border-gray-100 p-3 rounded-2xl"><p className="text-sm text-gray-400">Gépelés...</p></div></div>}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendMessage()} placeholder={`Kérdezz ${animal.name} viselkedéséről...`} className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
              <button onClick={handleSendMessage} className="bg-teal-600 text-white p-3 rounded-xl hover:bg-teal-700 transition-colors"><Send className="w-5 h-5" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdopterPortalPage;
